import { useEffect, useState } from "react";
import { Channel, StreamChat } from "stream-chat";
import { useServices } from "@core/servicesContext";

interface userConnectProps {
	profileId?: string;
	userToken?: string;
	userId?: string;
}

function toMemberId(id: unknown): string | null {
	if (id == null) return null;
	const value = String(id).trim();
	return value.length > 0 ? value : null;
}

export const useConnectUser = ({
	profileId,
	userToken,
	userId,
}: userConnectProps): { chatClient: StreamChat; loading: boolean; channel: Channel | null } | undefined => {
	const { chatClient } = useServices();
	const [loading, setLoading] = useState<boolean>(false);
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const [channel, setChannel] = useState<Channel | null>(null);

	useEffect(() => {
		if (!chatClient) return;

		let cancelled = false;

		const connectUser = async (): Promise<void> => {
			const connectedId = toMemberId(profileId);
			if (!connectedId || !userToken) {
				setIsConnected(false);
				return;
			}

			try {
				setLoading(true);
				await chatClient.connectUser({ id: connectedId }, userToken);
				if (!cancelled) {
					setIsConnected(true);
				}
			} catch (error) {
				console.error("Error connecting user", error);
				if (!cancelled) {
					setIsConnected(false);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		connectUser();

		return () => {
			cancelled = true;
		};
	}, [chatClient, profileId, userToken]);

	useEffect(() => {
		if (!chatClient || !isConnected) return;

		let cancelled = false;

		const startChannel = async (): Promise<void> => {
			const memberIds = [toMemberId(userId), toMemberId(profileId)].filter((id): id is string => id != null);

			if (memberIds.length < 2) return;
			if (!chatClient.userID) return;

			try {
				setLoading(true);
				const newChannel = chatClient.channel("messaging", {
					members: memberIds,
				});

				await newChannel.watch();
				if (!cancelled) {
					setChannel(newChannel);
				}
			} catch (error) {
				console.error("Error starting channel", error);
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		startChannel();

		return () => {
			cancelled = true;
		};
	}, [chatClient, isConnected, profileId, userId]);

	return chatClient ? { chatClient, loading, channel } : undefined;
};
